import { isEmpty } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { FocusedTextfield } from '~/src/dash/components/focused-textfield';
import { InfoTooltipIcon } from '~/src/dash/components/icons/InfoIcon';
import { IconText } from '~/src/dash/components/IconText';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractVisibleColumns,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { ButtonV2, Col, Cols, Panel, Row, Rows, spacing, Text } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingStateLoading } from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { IPlatformConnectDealExtensionProps } from '~/src/dash/views/platform-connect/common';
import { formatDealExtensionDataToLVTRows } from '~/src/dash/views/platform-connect/DraftOfferNew/config';
import { usePlatformConnectDraftOfferNewStore } from '~/src/dash/views/platform-connect/DraftOfferNew/contexts';
import { ISingleSelectOption, SingleSelect } from '~/src/dash/views/singleselect-view';

export const DealExtension = ({
  cfg,
  filters,
  selectedValues,
  onChange,
  loadState,
  dealExtensionOpportunitiesRows,
  fetchSellerPublishers,
  totalRowCount,
  handleClickOpenDrawer,
  disableDealExtOpportunities,
}: IPlatformConnectDealExtensionProps) => {
  const { isSuperUser } = useBaseStore();
  const { readAuthorization, writeAuthorization } = usePlatformConnectDraftOfferNewStore();
  const { dealExtension } = cfg;
  const cypressClassName = dealExtension.cypressClassName;
  const [currDealExtRows, setCurrDealExtRows] = useState(dealExtensionOpportunitiesRows);
  const [currPage, setCurrPage] = useState(dealExtension.page);
  const [rowsPerPage, setRowsPerPage] = useState<RowsPerPage>(dealExtension.rowsPerPage);

  useEffect(() => {
    setCurrDealExtRows(dealExtensionOpportunitiesRows.slice(0, rowsPerPage));
  }, [rowsPerPage, dealExtensionOpportunitiesRows]);

  useEffect(() => {
    setCurrDealExtRows(
      dealExtensionOpportunitiesRows.slice(
        rowsPerPage * currPage,
        rowsPerPage * currPage + rowsPerPage,
      ),
    );
  }, [currPage]);

  // update page number
  const handleChangePage = (_event: React.MouseEvent | null, page: number) => {
    setCurrPage(page);
  };

  // update rows per page
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedRowsPerPage = Number(event.target.value) as RowsPerPage;

    unstable_batchedUpdates(() => {
      setRowsPerPage(updatedRowsPerPage);
      setCurrPage(0);
    });
  };

  return (
    <>
      <Panel style={{ borderBottom: `1px solid ${publicaColors.lightGray}`, width: '775px' }}>
        <Rows>
          {cfg.dealExtension.filters.map((filter) => {
            return (
              <Row>
                <Cols style={{ alignItems: 'center' }}>
                  <Col>
                    <Cols
                      gap={filter.tooltipText ? spacing.extraSmall : spacing.none}
                      style={{
                        alignItems: 'center',
                        width: '200px',
                      }}
                    >
                      <Col>
                        <Text>{filter.label}</Text>
                      </Col>
                      {filter.tooltipText && (
                        <Col>
                          <InfoTooltipIcon tooltipText={filter.tooltipText} />
                        </Col>
                      )}
                    </Cols>
                  </Col>
                  {filter.key === 'deal' && (
                    <Col marginTop='-25px'>
                      <SingleSelect
                        label={filter.label}
                        name={filter.name}
                        style={{
                          width: '220px',
                        }}
                        items={filters[filter.key] as ISingleSelectOption[]}
                        value={selectedValues[filter.key] as string}
                        onChange={(_, value) => onChange(filter.key, value)}
                        itemWidth={200}
                      />
                    </Col>
                  )}
                  {filter.key === 'impressionsGoal' && (
                    <Col displayFlex style={{ alignItems: 'center', width: '100px' }}>
                      <FocusedTextfield
                        enableAlignRight
                        enableNumbersOnly
                        enablePositiveValuesOnly
                        type='number'
                        placeholder={'500000'}
                        multiline
                        label=''
                        name={filter.name}
                        value={selectedValues[filter.key]}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          onChange(filter.key, event.target.value)
                        }
                        error={undefined}
                      />
                    </Col>
                  )}
                </Cols>
              </Row>
            );
          })}
          <Row>
            <ButtonV2
              onClick={() =>
                fetchSellerPublishers({
                  rowsPerPage,
                  currPage,
                })
              }
              disabled={loadState === LoadingStateLoading || disableDealExtOpportunities}
            >
              <IconText
                bold
                small
                gap={spacing.extraSmall}
                icon='flag'
                color={publicaColors.white}
                text='View opportunities'
                style={{
                  padding: 'none',
                }}
              />
            </ButtonV2>
          </Row>
        </Rows>
      </Panel>
      {!isEmpty(dealExtensionOpportunitiesRows) && (
        <Panel>
          <Row noVerticalPadding>
            <Text>To start, click on "Make an Offer" to propose a CPM and send.</Text>
          </Row>
        </Panel>
      )}
      {!isEmpty(dealExtensionOpportunitiesRows) && (
        <Panel noPadding highlighted style={{ width: '750px' }}>
          <Rows>
            <Rows>
              {/* TABLE */}
              <Row noHorizontalPadding>
                <ListViewTable
                  customFunctions={{ handleClickOpenDrawer }}
                  activeColumnKeys={Object.values(
                    extractVisibleColumns(
                      dealExtension.table.columns,
                      dealExtension.activeColumnKeys || [],
                      isSuperUser,
                    ),
                  )
                    .filter((column: any) => column.selected)
                    .map((column: any) => column.ID)}
                  columns={cfg.dealExtension.table.columns}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                  handleOnClickColumnHeader={() => {
                    return;
                  }}
                  isReadAuthorized={readAuthorization.isAuthorized}
                  isSuperUser={isSuperUser}
                  isWriteAuthorized={writeAuthorization.isAuthorized}
                  loadState={loadState}
                  order={dealExtension.orderDirection}
                  orderBy={dealExtension.orderBy}
                  page={currPage}
                  rows={formatDealExtensionDataToLVTRows(currDealExtRows, cfg)}
                  rowsPerPage={rowsPerPage}
                  totalRowCount={totalRowCount}
                  className={cypressClassName}
                />
              </Row>
            </Rows>
          </Rows>
        </Panel>
      )}
    </>
  );
};
